// Which side of Stitch the user is on: company, partner or staff. The side comes from the page when it is a
// company (/, /card, /team ...: app/paths.ts), partner (/partner) or staff (/staff) page; on shared pages (/directory,
// /agent/connect)
// it is the last side the user chose, kept for the tab (sessionStorage), else the first side they hold.
// Only the switcher changes it.
import { useLayoutEffect, useSyncExternalStore } from 'react'
import { useLocation } from 'react-router-dom'
import { useMe, type Me } from '../lib'
import { isCompanyPage } from './paths'

export type Side = 'company' | 'partner' | 'staff'
const KEY = 'pr_side'
const listeners = new Set<() => void>()

function read(): Side | null {
  try {
    const v = window.sessionStorage.getItem(KEY)
    return v === 'company' || v === 'partner' || v === 'staff' ? v : null
  } catch {
    return memory
  }
}
let memory: Side | null = null

export function setSide(side: Side): void {
  memory = side
  try {
    window.sessionStorage.setItem(KEY, side)
  } catch {
    // storage blocked: the side lasts while the page stays open
  }
  listeners.forEach((l) => l())
}

export function sideOfPath(pathname: string): Side | null {
  if (isCompanyPage(pathname)) return 'company'
  if (pathname === '/partner' || pathname.startsWith('/partner/')) return 'partner'
  if (pathname === '/staff' || pathname.startsWith('/staff/')) return 'staff'
  return null
}

/** The sides this user holds, in landing order. */
export function heldSides(me: Me | undefined): Side[] {
  if (!me?.user) return []
  return [
    ...(me.companies.length ? (['company'] as Side[]) : []),
    ...(me.partners.length ? (['partner'] as Side[]) : []),
    ...(me.is_staff ? (['staff'] as Side[]) : []),
  ]
}

export const SIDE_HOME: Record<Side, string> = { company: '/', partner: '/partner', staff: '/staff' }
export const SIDE_TAG: Record<Side, string> = { company: 'Company', partner: 'Partner', staff: 'Staff' }

/** null when signed out (or when the user holds nothing yet and is on a shared page). */
export function useSide(): { side: Side | null; held: Side[] } {
  const { pathname } = useLocation()
  const { me } = useMe()
  const stored = useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb) } },
    read,
  )
  const held = heldSides(me)
  // A page of a side the user does not hold (a founder opening /partner) keeps their own side, so the bar shows
  // their nav and switcher, not five partner links that each say "not on a partner team".
  const path = sideOfPath(pathname)
  const onPage = path && held.includes(path) ? path : null
  const side: Side | null = !me?.user ? null : onPage ?? (stored && held.includes(stored) ? stored : held[0] ?? null)
  // Opening a company, partner or staff page makes it the side, so a later shared page keeps it. A layout effect
  // stores it before the page paints: a plain effect ran after paint, so leaving at once lost it (nav.spec flake).
  useLayoutEffect(() => {
    if (me?.user && onPage && stored !== onPage) setSide(onPage)
  }, [me?.user, onPage, stored])
  return { side, held }
}
